import { Routes, Route, Navigate } from "react-router-dom"
import Index from "@/pages/Index"
import Login from "@/pages/Login"
import SignUp from "@/pages/SignUp"
import ForgotPassword from "@/pages/ForgotPassword"
import ResetPassword from "@/pages/ResetPassword"
import Moradores from "@/pages/Moradores"
import Colaboradores from "@/pages/Colaboradores"
import Unidades from "@/pages/Unidades"
import Visitantes from "@/pages/Visitantes"
import Encomendas from "@/pages/Encomendas"
import Ocorrencias from "@/pages/Ocorrencias"
import Pets from "@/pages/Pets"
import Avisos from "@/pages/Avisos"
import Mensagens from "@/pages/Mensagens"
import AreasComuns from "@/pages/AreasComuns"
import Agenda from "@/pages/Agenda"
import PontoEletronico from "@/pages/PontoEletronico"
import FolhasPonto from "@/pages/FolhasPonto"
import Relatorios from "@/pages/Relatorios"
import Configuracoes from "@/pages/Configuracoes"
import SuperAdmin from "@/pages/SuperAdmin"
import Morador from "@/pages/Morador"
import Reunioes from "@/pages/Reunioes"
import Reuniao from "@/pages/Reuniao"
import Financeiro from "@/pages/Financeiro"
import Acessos from "@/pages/Acessos"
import Destinos from "@/pages/Destinos"
import Landing from "@/pages/Landing"
import Demonstracao from "@/pages/Demonstracao"
import Sobre from "@/pages/Sobre"
import PoliticaPrivacidade from "@/pages/PoliticaPrivacidade"
import TermosDeUso from "@/pages/TermosDeUso"
import Encarregado from "@/pages/Encarregado"
import NotFound from "@/pages/NotFound"
import ServicoDetalhe from "@/pages/servicos/ServicoDetalhe"
import GestaoCondominios from "@/pages/servicos/GestaoCondominios"
import GestaoComercial from "@/pages/GestaoComercial"
import Propostas from "@/pages/superadmin/Propostas"
import Contratos from "@/pages/superadmin/Contratos"
import ReunioesComercias from "@/pages/superadmin/ReunioesComercias"
import HorasTecnicas from "@/pages/superadmin/HorasTecnicas"
import PropostaPublica from "@/pages/superadmin/PropostaPublica"
import ContratoPublico from "@/pages/superadmin/ContratoPublico"
import SuperAdminCondominiums from "@/pages/superadmin/Condominiums"
import SuperAdminUsers from "@/pages/superadmin/Users"
import SuperAdminLicenses from "@/pages/superadmin/Licenses"
import SuperAdminData from "@/pages/superadmin/Data"
import SuperAdminAnalytics from "@/pages/superadmin/Analytics"
import ProtectedRoute from "./ProtectedRoute"
import { useAuth } from "@/hooks/useAuth"
import { Loader2 } from "lucide-react"
import Preloader from "./Preloader"
import { routeAccess } from "@/lib/authorization"

const SmartLanding = () => {
  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean }
  const isPWA = window.matchMedia('(display-mode: standalone)').matches
    || navigatorWithStandalone.standalone === true;

  const { user, loading } = useAuth();

  if (isPWA) {
    if (loading) {
      return <Preloader />;
    }
    if (user) return <Navigate to="/dashboard" replace />;
    return <Navigate to="/login" replace />;
  }

  return <Landing />;
};

const RouterWrapper = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<SmartLanding />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/landing" element={<Landing />} />
      <Route path="/demonstracao" element={<Demonstracao />} />
      <Route path="/sobre" element={<Sobre />} />
      <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
      <Route path="/termos-de-uso" element={<TermosDeUso />} />
      <Route path="/encarregado-de-dados" element={<Encarregado />} />
      <Route path="/servicos/gestao-condominios" element={<GestaoCondominios />} />
      <Route path="/servicos/:slug" element={<ServicoDetalhe />} />
      {/* Proposta pública — sem autenticação */}
      <Route path="/p/:token" element={<PropostaPublica />} />
      <Route path="/c/:token" element={<ContratoPublico />} />
      
      {/* Protected routes */}
      <Route path="/moradores" element={<ProtectedRoute accessRule={routeAccess.residents}><Moradores /></ProtectedRoute>} />
      <Route path="/colaboradores" element={<ProtectedRoute accessRule={routeAccess.staff}><Colaboradores /></ProtectedRoute>} />
      <Route path="/unidades" element={<ProtectedRoute accessRule={routeAccess.units}><Unidades /></ProtectedRoute>} />
      <Route path="/mensagens" element={<ProtectedRoute accessRule={routeAccess.messages}><Mensagens /></ProtectedRoute>} />
      <Route path="/visitantes" element={<ProtectedRoute accessRule={routeAccess.visitors}><Visitantes /></ProtectedRoute>} />
      <Route path="/encomendas" element={<ProtectedRoute accessRule={routeAccess.packages}><Encomendas /></ProtectedRoute>} />
      <Route path="/ocorrencias" element={<ProtectedRoute accessRule={routeAccess.incidents}><Ocorrencias /></ProtectedRoute>} />
      <Route path="/agenda" element={<ProtectedRoute accessRule={routeAccess.agenda}><Agenda /></ProtectedRoute>} />
      <Route path="/avisos" element={<ProtectedRoute accessRule={routeAccess.notices}><Avisos /></ProtectedRoute>} />
      <Route path="/financeiro" element={<ProtectedRoute accessRule={routeAccess.finance}><Financeiro /></ProtectedRoute>} />
      <Route path="/areas-comuns" element={<ProtectedRoute accessRule={routeAccess.commonAreas}><AreasComuns /></ProtectedRoute>} />
      <Route path="/pets" element={<ProtectedRoute accessRule={routeAccess.pets}><Pets /></ProtectedRoute>} />
      <Route path="/relatorios" element={<ProtectedRoute accessRule={routeAccess.reports}><Relatorios /></ProtectedRoute>} />
      <Route path="/acessos" element={<ProtectedRoute accessRule={routeAccess.access}><Acessos /></ProtectedRoute>} />
      <Route path="/destinos" element={<ProtectedRoute accessRule={routeAccess.destinations}><Destinos /></ProtectedRoute>} />
      <Route path="/ponto-digital" element={<ProtectedRoute accessRule={routeAccess.timeClock}><PontoEletronico /></ProtectedRoute>} />
      <Route path="/ponto-eletronico" element={<ProtectedRoute accessRule={routeAccess.timeClock}><Navigate to="/ponto-digital" replace /></ProtectedRoute>} />
      <Route path="/folhas-ponto" element={<ProtectedRoute accessRule={routeAccess.timesheets}><FolhasPonto /></ProtectedRoute>} />
      <Route path="/reunioes" element={<ProtectedRoute accessRule={routeAccess.meetings}><Reunioes /></ProtectedRoute>} />
      <Route path="/reunioes/:id" element={<ProtectedRoute accessRule={routeAccess.meetings}><Reuniao /></ProtectedRoute>} />
      <Route path="/configuracoes" element={<ProtectedRoute accessRule={routeAccess.settings}><Configuracoes /></ProtectedRoute>} />
      <Route path="/superadmin" element={<ProtectedRoute accessRule={routeAccess.superadmin}><SuperAdmin /></ProtectedRoute>} />
      <Route path="/superadmin/organizacoes" element={<ProtectedRoute accessRule={routeAccess.superadmin}><SuperAdminCondominiums /></ProtectedRoute>} />
      <Route path="/superadmin/usuarios" element={<ProtectedRoute accessRule={routeAccess.superadmin}><SuperAdminUsers /></ProtectedRoute>} />
      <Route path="/superadmin/licencas" element={<ProtectedRoute accessRule={routeAccess.superadmin}><SuperAdminLicenses /></ProtectedRoute>} />
      <Route path="/superadmin/dados" element={<ProtectedRoute accessRule={routeAccess.superadmin}><SuperAdminData /></ProtectedRoute>} />
      <Route path="/superadmin/analytics" element={<ProtectedRoute accessRule={routeAccess.superadmin}><SuperAdminAnalytics /></ProtectedRoute>} />

      {/* Gestão Comercial — superadmin only */}
      <Route path="/gestao-comercial" element={<ProtectedRoute accessRule={routeAccess.superadmin}><GestaoComercial /></ProtectedRoute>} />
      <Route path="/gestao-comercial/propostas" element={<ProtectedRoute accessRule={routeAccess.superadmin}><Propostas /></ProtectedRoute>} />
      <Route path="/gestao-comercial/contratos" element={<ProtectedRoute accessRule={routeAccess.superadmin}><Contratos /></ProtectedRoute>} />
      <Route path="/gestao-comercial/reunioes" element={<ProtectedRoute accessRule={routeAccess.superadmin}><ReunioesComercias /></ProtectedRoute>} />
      <Route path="/gestao-comercial/horas" element={<ProtectedRoute accessRule={routeAccess.superadmin}><HorasTecnicas /></ProtectedRoute>} />

      {/* Dashboard alias route */}
      <Route path="/dashboard" element={<ProtectedRoute accessRule={routeAccess.dashboard}><Index /></ProtectedRoute>} />
      
      {/* 404 route */}
      <Route path="*" element={<NotFound />} />
    </Routes>

  )
}

export default RouterWrapper
